import { Box, Stack, Typography } from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { palette, spaces } from '../../../../../assets/styles/themes/tokens';
import { useTranslation } from 'react-i18next';
import { aiAssistantContext, AIAssistantDisplayMode } from '../../../../../state/AIAssistantContextProvider';
import { ActiveView, PODCAST_GENERATION_STARTED_MESSAGE } from '../../constants';
import AIAssistantGeneratingIndicator from './AIAssistantGeneratingIndicator';
import { copyElementContent } from '../../../../../utils/copyElementContent';
import AIAssistantIconButton from '../../../Dialog/AIAssistantIconButton';
import { useAIAssistantLatestMessageHeight } from '../../../Dialog/hooks/useAIAssistantLatestMessageHeight';
import { ConversationMessage } from '@kimuno/api-sdk/lib/types/common/Conversation';
import { useSelector } from 'react-redux';
import {
  getConversationDocument,
  getCurrentConversation,
  getDocumentById,
  getPodcastById,
  isDocumentLoading as isDocumentLoadingSelector,
} from '../../../../../reducers/aiAssistant.selectors';
import useAction from '../../../../../utils/useAction';
import {
  saveMessageSentiment as saveMessageSentimentAction,
  getGeneratedDocument as getGeneratedDocumentAction,
  getGeneratedPodcast as getGeneratedPodcastAction,
} from '../../../../../actions/aiAssistant.actions';
import services from '../../../../../services';
import { ConversationNewMessage } from '../../../../../reducers/aiAssistant.reducers';
import remarkGfm from 'remark-gfm';
import { OnboardingHint } from '../../../../OnboardingHint';
import { onboardingHints } from '../../../../../constants';
import useScreenSize from '../../../../../utils/useScreenSize';
import useIsPodcastLoadingViewActive from '../useIsPodcastLoadingViewActive';
import { ampliTrackMessageDisplayedEvent, MessageType } from '../../../../../services/track.service';
import ChatMessageArtifactCard, { CHAT_MESSAGE_ARTIFACT_CARD_VARIANT } from '../../../../cards/ChatMessageArtifactCard';
import ExtendedChatMessageArtifactCard from '../../../../cards/ChatMessageArtifactCard/ExtendedChatMessageArtifactCard';
import ExtendedPodcastArtifactCard from '../../../../cards/ChatMessageArtifactCard/ExtendedPodcastArtifactCard';
import { PodcastMessageContext } from '../../podcastTypes';
import { aiAssistantEditorContext, PodcastDetailsData } from '../../../../../state/AIAssistantEditorProvider';
import { GeneratedPodcastStageStatus } from '@kimuno/api-sdk/lib/types/responses/Ai';
import { StoreType } from '../../../../../reducers';
import { downloadHtmlAsPdf, downloadHtmlAsDocx } from '../../../../../utils/documentExport';
import { convertMarkdownToHtmlForPdf } from '../../../../utils/contentConverter';
import { sanitizeFilename } from '../../utils';
import useRouter from '../../../../../utils/useRouter';
import { getAIAssistantPageSource } from '../../../../../utils/getAIAssistantPageSource';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { getMarkdownComponents, preprocessMarkdownText } from '../../../../../utils/markdown';
import ImagePreviewOverlay from '../../ImagePreviewOverlay/ImagePreviewOverlay';
import ChatImage from './ChatImage';

const IMAGE_MARKDOWN_REGEX = /!\[[^\]]*\]\([^)]+\)/;

// Wrapper component for handling hint close functionality
const EditButtonWithHint: React.FC<{
  handleHintClose?: () => void;
  onClick?: () => void;
  [key: string]: any;
}> = React.forwardRef<HTMLButtonElement, any>(({ handleHintClose, onClick, ...props }, ref) => (
  <AIAssistantIconButton
    {...props}
    ref={ref}
    onClick={() => {
      handleHintClose?.();
      onClick?.();
    }}
  />
));

EditButtonWithHint.displayName = 'EditButtonWithHint';

const AIAssistantMessage: React.FC<{
    message: ConversationMessage | ConversationNewMessage
    isLast: boolean
    withRegenerateButton: boolean
    withFooter?: boolean
    isStreaming?: boolean
}> = ({ message, isLast, withRegenerateButton, withFooter = true, isStreaming = false }) => {
  const { t } = useTranslation();
  const messageText = preprocessMarkdownText(message.content[0].text, isStreaming);
  const sentiment = 'sentimentFeedback' in message ? message.sentimentFeedback : null;
  const messageId = 'id' in message ? message.id : null;
  const showDocumentCard = !!message.context?.generatedDocumentId && !!message.context?.generatedDocumentTitle;
  const generatedDocumentId = message.context?.generatedDocumentId;
  const messageActionsRef = useRef<HTMLDivElement | null>(null);
  const [isEditButtonVisible, setIsEditButtonVisible] = useState(false);
  const {
    isExtended,
    regenerateResponse,
    currentSessionHints,
    addCurrentSessionHint,
    dialogContentRef,
    displayMode,
    activeView,
    generatingPodcast,
    isPodcastV1UxEnabled,
    preparePodcastNavigation
  } = useContext(aiAssistantContext);
  const isPageMode = displayMode === AIAssistantDisplayMode.PAGE;
  // While the podcast is being generated (polling), show a dynamic loading
  // indicator instead of the persisted static message text (mirrors image gen).
  const generatedPodcastId =
    (message.context as PodcastMessageContext | undefined)?.generatedPodcastId;
  const generatedPodcast = useSelector((state: StoreType) => getPodcastById(state, generatedPodcastId ?? null));
  // Only the generating podcast's own message counts as generating — other podcast
  // messages keep their expanded cards. generatingPodcast arrives with the first poll
  // tick; until then fall back to the view alone so a fresh podcast shows its
  // indicator immediately. A podcast generated mid-chat (refinement) never
  // enters the loading view — its stored LOADING status is the signal instead.
  const isPodcastGenerating = !!generatedPodcastId && (
    generatedPodcast?.data?.status === GeneratedPodcastStageStatus.LOADING ||
    (activeView === ActiveView.PODCAST_LOADING &&
      (!generatingPodcast || generatingPodcast.id === generatedPodcastId))
  );
  const showPodcastCard = !!generatedPodcastId;
  // WOR-930: filter the canned "podcast wird erstellt" placeholder out of the
  // chat — the loading indicator + podcast card convey progress instead. Match
  // the raw content (messageText is preprocessed with trailing whitespace).
  const isPodcastStartedMessage =
    message.content?.[0]?.text === PODCAST_GENERATION_STARTED_MESSAGE;
  // Copy/Edit act on the text response and don't apply to artifact messages
  // (documents/podcasts) — those are handled from the artifact card itself.
  const hasArtifactCard = showDocumentCard || showPodcastCard;
  const { match: { location: { pathname } } } = useRouter();

  const {
    openTextEditor,
    closeTextEditor,
    isTextEditorOpen,
    documentId: textEditorDocumentId,
    editorCloseScrollTarget,
    activeDocumentCardRef,
    activeImageRef,
    imageScrollTarget,
    podcastDetails,
    openPodcast,
    activePodcastId,
  } = useContext(aiAssistantEditorContext);
  // When this message's podcast is the open one, reuse its loaded title on the card
  // (so an optimistic rename shows through); otherwise the card falls back to a placeholder.
  const activePodcastDetails =
    podcastDetails && podcastDetails.id === generatedPodcastId ? podcastDetails : null;
  // Every card resolves its own podcast from the store (mirroring documents), so all
  // podcast cards expand instead of just the open one. The label fields stay empty —
  // the card doesn't render them, and the detail view fetches its own complete data.
  const podcastCardData = useMemo((): PodcastDetailsData | null => {
    if (activePodcastDetails) return activePodcastDetails;
    const stored = generatedPodcast?.data;
    if (!generatedPodcastId || !stored) return null;
    return {
      id: generatedPodcastId,
      title: stored.title,
      subject: '',
      grade: '',
      language: '',
      format: '',
      transcript: stored.script?.content ?? '',
      audioUrl: stored.audio?.url,
      audioGenerationFailed: stored.status === GeneratedPodcastStageStatus.ERROR,
    };
  }, [activePodcastDetails, generatedPodcast?.data, generatedPodcastId]);
  // Title the poll surfaces mid-generation, before the detail (and its title) is loaded.
  const generatingPodcastTitle =
    generatingPodcast && generatingPodcast.id === generatedPodcastId ? generatingPodcast.title : undefined;
  const handlePodcastCardClick = () => {
    if (generatedPodcastId) openPodcast({ podcastId: generatedPodcastId });
  };
  const handlePodcastCardPlay = () => {
    if (generatedPodcastId) openPodcast({ podcastId: generatedPodcastId, autoPlay: true });
  };
  // Clicking a still-generating card is explicit watch intent — enter the same
  // loading view as the creation flow. An open editor pane must yield
  // the shell first. Page mode only: the dialog has no loading pane or poll.
  const handleGeneratingPodcastCardClick = () => {
    if (!generatedPodcastId) return;
    if (isTextEditorOpen) closeTextEditor();
    preparePodcastNavigation(true, generatedPodcastId);
  };
  const compactPodcastCardClick = isPodcastGenerating
    ? (isPageMode ? handleGeneratingPodcastCardClick : undefined)
    : handlePodcastCardClick;
  // Highlight this card while its own podcast is generating (parity with a streaming
  // document) — gated on isPodcastGenerating because generatingPodcast lingers after
  // generation, and keyed by id so only the generating card lights up.
  const isGeneratingThisPodcast = isPodcastGenerating && generatingPodcast?.id === generatedPodcastId;
  // ...or when its detail is the currently open one.
  const isOpenPodcast = isTextEditorOpen && activePodcastId === generatedPodcastId;
  const currentConversation = useSelector(getCurrentConversation);
  const generatedDocument = useSelector((state: StoreType) => getDocumentById(state, generatedDocumentId ?? null));
  const streamDocument = useSelector(getConversationDocument);
  const isDocumentStopped = streamDocument 
  && streamDocument.status === 'stopped' 
  && generatedDocumentId === streamDocument.id;
  const currentConversationId = currentConversation?.data?.id;
  const saveMessageSentiment = useAction(saveMessageSentimentAction);
  const getGeneratedDocument = useAction(getGeneratedDocumentAction);
  const getGeneratedPodcast = useAction(getGeneratedPodcastAction);
  const aiMessageRef = useRef<HTMLDivElement | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [previewImage, setPreviewImage] = useState<{ src: string; alt?: string } | null>(null);
  const imageOnClick = useCallback(
    (src: string, alt?: string) => setPreviewImage({ src, alt }),
    []
  );

  const deriveUserPrompt = useCallback((): string | null => {
    const messages = currentConversation?.data?.messages || [];
    const currentMessageId = 'id' in message ? message.id : undefined;
    const currentMessageIndex = messages.findIndex(
      (msg: ConversationMessage | ConversationNewMessage) =>
        ('id' in msg ? msg.id : undefined) === currentMessageId
    );
    const connectedUserMessage = currentMessageIndex > 0 ? messages[currentMessageIndex - 1] : null;
    if (!connectedUserMessage) return null;
    return connectedUserMessage.content[0].text;
  }, [currentConversation, message]);

  const deriveResponseFeature = useCallback((): string[] => {
    const features: string[] = [];
    if (message.context?.generatedDocumentId) features.push('document generation');
    const messageText = message.content?.[0]?.text ?? '';
    const documentContent = generatedDocument?.data?.content ?? '';
    if (IMAGE_MARKDOWN_REGEX.test(messageText) || IMAGE_MARKDOWN_REGEX.test(documentContent)) {
      features.push('image generation');
    }
    return features;
  }, [message, generatedDocument?.data?.content]);

  const trackActionPerformed = useCallback(
    (params: { type: 'copy' | 'download' | 'regenerate' | 'edit'; feature: string[] }) => {
      if (!currentConversationId || !messageId) return;
      const userPrompt = deriveUserPrompt();
      if (userPrompt === null) return;
      services.track.ampliTrackAIAssistantActionPerformedEvent({
        conversationId: currentConversationId,
        requestId: messageId,
        pageSource: getAIAssistantPageSource(pathname),
        type: params.type,
        feature: params.feature,
        userPrompt,
      });
    },
    [currentConversationId, messageId, deriveUserPrompt, pathname]
  );

  const handleImageDownloadTrack = useCallback(
    () => trackActionPerformed({ type: 'download', feature: ['image generation'] }),
    [trackActionPerformed]
  );

  const handleImageCopyTrack = useCallback(
    () => trackActionPerformed({ type: 'copy', feature: ['image generation'] }),
    [trackActionPerformed]
  );

  const handleResponseCopyTrack = useCallback(
    () => trackActionPerformed({ type: 'copy', feature: deriveResponseFeature() }),
    [trackActionPerformed, deriveResponseFeature]
  );

  const handleRegenerateTrack = useCallback(
    () => trackActionPerformed({ type: 'regenerate', feature: deriveResponseFeature() }),
    [trackActionPerformed, deriveResponseFeature]
  );

  const handleEditTrack = useCallback(
    () => trackActionPerformed({ type: 'edit', feature: deriveResponseFeature() }),
    [trackActionPerformed, deriveResponseFeature]
  );

  const markdownComponents = useMemo(
    () => ({
      ...getMarkdownComponents(undefined, imageOnClick),
      img: ({ src, alt }: React.ImgHTMLAttributes<HTMLImageElement>) => (
        <ChatImage
          src={src}
          alt={alt}
          onClick={src ? () => imageOnClick(src, alt) : undefined}
          onCopy={handleImageCopyTrack}
          onDownload={handleImageDownloadTrack}
        />
      ),
    }),
    [imageOnClick, handleImageCopyTrack, handleImageDownloadTrack]
  );
  const messageHeight = useAIAssistantLatestMessageHeight();
  const { isXsScreen, isSmScreen, isMdScreen, isLgScreen } = useScreenSize();
  const isLargeScreen = isSmScreen || isMdScreen || isLgScreen;
  const isPodcastLoadingPaneActive = useIsPodcastLoadingViewActive();
  // Expanded only in the wide chat: any open pane (editor or loading view)
  // narrows the chat, and a generating podcast shows the compact card instead.
  const showExtendedPodcastCard = isPodcastV1UxEnabled && isLargeScreen && isPageMode
    && !!podcastCardData && !isTextEditorOpen && !isPodcastLoadingPaneActive && !isPodcastGenerating;
  const isDocumentLoading = useSelector(state => isDocumentLoadingSelector(state, generatedDocumentId ?? null));

  const handleCopyButtonClick = () => {
    if (!aiMessageRef.current) return;
    handleResponseCopyTrack();
    copyElementContent(aiMessageRef.current);
    setIsCopied(true);
  };

  const handleRegenerateButtonClick = () => {
    handleRegenerateTrack();
    regenerateResponse();
  };

  const handleSentimentClick = (sentimentFeedback: 'positive' | 'negative') => {
    if (sentiment || !currentConversationId || !messageId) {
      return;
    }

    const { data } = currentConversation;
    const messages = data?.messages || [];
    const templatePromptId = message.context?.templatePromptId;

    const currentMessageIndex = messages.findIndex(msg => msg.id === message.id);
    const connectedUserMessage = currentMessageIndex > 0 ? messages[currentMessageIndex - 1] : null;
    
    if (!connectedUserMessage) {
      return;
    }

    const promptType = templatePromptId ? 'template' : 'custom';
    const prompt = templatePromptId ? templatePromptId : connectedUserMessage.content[0].text;
    const pageSource = getAIAssistantPageSource(pathname);

    services.track.ampliTrackAIAssistantFeedbackSubmittedEvent({
      conversationId: currentConversationId,
      requestId: messageId,
      feedbackValue: sentimentFeedback,
      promptType,
      prompt,
      pageSource,
      feature: deriveResponseFeature(),
    });

    saveMessageSentiment({
      conversationId: currentConversationId,
      messageId: messageId,
      sentimentFeedback
    });
  };

  useEffect(() => {
    if (isCopied) {
      setTimeout(() => setIsCopied(false), 3000);
    } 
  }, [isCopied]);

  // Intersection Observer to detect when edit button is visible
  useEffect(() => {
    if (!messageActionsRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsEditButtonVisible(entry.isIntersecting);
        });
      },
      {
        root: dialogContentRef?.current || null, // Use the messages container as the root
        threshold: 1, // Trigger when the button is visible
        rootMargin: '0px',
      }
    );

    observer.observe(messageActionsRef.current);

    return () => {
      observer.disconnect();
    };
  }, [dialogContentRef]);

  const handleTextEditorHintDisplayed  = () => { 
    ampliTrackMessageDisplayedEvent('muki edit button hint', MessageType.HINT, 1);
    addCurrentSessionHint(onboardingHints.aiAssistant.textEditor);
  };
  
  // Load document when conditions are met for showing ExtendedDocumentCard
  useEffect(() => {
    if (
      generatedDocumentId &&
      isLargeScreen &&
      isPageMode &&
      !isTextEditorOpen &&
      showDocumentCard &&
      !generatedDocument?.data &&
      !isDocumentLoading
    ) {
      getGeneratedDocument({ documentId: generatedDocumentId });
    }
  }, [
    generatedDocumentId,
    isLargeScreen,
    isPageMode,
    isTextEditorOpen,
    showDocumentCard,
    generatedDocument?.data,
    isDocumentLoading,
    getGeneratedDocument
  ]);

  // Hydrate each message's podcast: the card title (podcast messages carry none in
  // context, unlike documents) and the stored status that drives the generating
  // state and background poll — so it must run on every screen size, even while
  // the editor is open. Fetch only while the store has no entry at all — an ERROR
  // entry stays put, so a persistent failure can't refetch-loop.
  useEffect(() => {
    if (
      generatedPodcastId &&
      isPodcastV1UxEnabled &&
      isPageMode &&
      !isPodcastGenerating &&
      !generatedPodcast
    ) {
      getGeneratedPodcast({ podcastId: generatedPodcastId });
    }
  }, [
    generatedPodcastId,
    isPodcastV1UxEnabled,
    isPageMode,
    isPodcastGenerating,
    generatedPodcast,
    getGeneratedPodcast
  ]);
  
  const documentTitle = generatedDocument?.data?.title || message.context?.generatedDocumentTitle || '';
  const isScrollTarget =
    (editorCloseScrollTarget?.documentId && generatedDocumentId === editorCloseScrollTarget.documentId) ||
    (editorCloseScrollTarget?.messageId && messageId === editorCloseScrollTarget.messageId);
  const isPodcastScrollTarget =
    !!editorCloseScrollTarget?.podcastId && generatedPodcastId === editorCloseScrollTarget.podcastId;
  const isImageScrollTarget = imageScrollTarget && messageId === imageScrollTarget;

  const handleEditClick = () => {
    if (!messageId || !currentConversationId) return;
    const pageSource = getAIAssistantPageSource(pathname);

    openTextEditor({ messageId });
    services.track.ampliTrackTextEditorOpenedEvent(currentConversationId, 'chat', pageSource);
  };

  const handleEditButtonClick = () => {
    handleEditTrack();
    handleEditClick();
  };

  const handleDocumentClick = () => {
    if (!currentConversation?.data?.id || !generatedDocumentId) return;
    const pageSource = getAIAssistantPageSource(pathname);

    openTextEditor({ documentId: generatedDocumentId });
    services.track.ampliTrackTextEditorOpenedEvent(currentConversation.data.id, 'document', pageSource);
  };

  const getDocumentDownloadData = () => {
    const content = generatedDocument?.data?.content;
    if (!content) return null;
    const filename = generatedDocument?.data?.title || message.context?.generatedDocumentTitle || 'document';
    const html = convertMarkdownToHtmlForPdf(content);
    const sanitizedFilename = sanitizeFilename(filename);
    return { html, sanitizedFilename };
  };

  const handleDocumentDownloadPdf = async () => {
    const data = getDocumentDownloadData();
    if (data) {
      await downloadHtmlAsPdf(data.html, data.sanitizedFilename);
      if (currentConversationId) {
        services.track.ampliTrackTextEditorFileExportedEvent({
          conversationId: currentConversationId,
          exportType: 'download',
          exportFormat: 'pdf',
          feature: deriveResponseFeature(),
        });
      }
    }
  };

  const handleDocumentDownloadDocx = async () => {
    const data = getDocumentDownloadData();
    if (data) {
      await downloadHtmlAsDocx(data.html, data.sanitizedFilename);
      if (currentConversationId) {
        services.track.ampliTrackTextEditorFileExportedEvent({
          conversationId: currentConversationId,
          exportType: 'download',
          exportFormat: 'docx',
          feature: deriveResponseFeature(),
        });
      }
    }
  };

  return (
    <Stack
      // Answer-edit targets have no card to carry the scroll ref, so the message root holds it.
      ref={
        isImageScrollTarget
          ? activeImageRef
          : isScrollTarget && !showDocumentCard ? activeDocumentCardRef : undefined
      }
      sx={isLast ? { minHeight: messageHeight } : {}}
      data-testid='ai-assistant-message-root'
      data-is-last={isLast}
    >
      {('isError' in message && message.isError) ? (
        <Typography 
          data-testid='ai-assistant-chat-error-message'
          variant='body2'
          sx={{ color: palette.text.secondary }}>
          {messageText}
        </Typography>
      ) : (
        <>
          <Box ref={aiMessageRef} data-testid='ai-assistant-message__ai-answer-content'>
            {showDocumentCard && (
              <Stack gap={spaces.sm} sx={{ marginTop: spaces.md, marginBottom: spaces.md }}>
                {isDocumentStopped && (
                  <Box>
                    <Typography variant='body2' sx={{ marginBottom: spaces.xs }}>
                      {t('Generation stopped.')}
                    </Typography>
                    <Typography variant='body2' sx={{ marginBottom: spaces.xs }}>
                      {t('The document was not fully created, but already contains initial content.')}
                    </Typography>
                    <Typography variant='body2'>
                      {t('If you want, you can open the document directly in the editor, '
                        + 'customize it and continue creation.')}
                    </Typography>
                  </Box>
                )}
                {isLargeScreen && isPageMode && generatedDocument?.data
                  && !isTextEditorOpen && !isPodcastLoadingPaneActive ? (
                    <ExtendedChatMessageArtifactCard
                      ref={isScrollTarget ? activeDocumentCardRef : undefined}
                      document={{
                        id: generatedDocumentId!,
                        title: documentTitle,
                        content: generatedDocument.data.content,
                        status: 'done'
                      }}
                      onEdit={handleDocumentClick}
                      onDownloadPdf={handleDocumentDownloadPdf}
                      onDownloadDocx={handleDocumentDownloadDocx}
                    />
                  ) : (
                    <ChatMessageArtifactCard
                      ref={isScrollTarget ? activeDocumentCardRef : undefined}
                      title={documentTitle || t('Generating document...')}
                      subtitle={isDocumentLoading ? t('Loading...') : t('Click to edit')}
                      onClick={isDocumentLoading ? undefined : handleDocumentClick}
                      isActive={isTextEditorOpen && textEditorDocumentId === generatedDocumentId}
                    />
                  )}
              </Stack>
            )}
            {isPodcastGenerating && (
              <AIAssistantGeneratingIndicator
                text={t('The podcast is being created...')}
                dataTestId='podcast-is-being-generated-text'
              />
            )}
            {showPodcastCard && (
              <Stack gap={spaces.sm} sx={{ marginTop: spaces.md, marginBottom: spaces.md }}>
                {showExtendedPodcastCard && podcastCardData ? (
                  <ExtendedPodcastArtifactCard
                    ref={isPodcastScrollTarget ? activeDocumentCardRef : undefined}
                    podcast={podcastCardData}
                    onShowDetails={handlePodcastCardClick}
                    onPlay={handlePodcastCardPlay}
                  />
                ) : (
                  <ChatMessageArtifactCard
                    ref={isPodcastScrollTarget ? activeDocumentCardRef : undefined}
                    variant={CHAT_MESSAGE_ARTIFACT_CARD_VARIANT.PODCAST}
                    title={podcastCardData?.title || generatingPodcastTitle || t('Podcast')}
                    subtitle={t('Open detail view')}
                    onClick={compactPodcastCardClick}
                    isActive={isGeneratingThisPodcast || isOpenPodcast}
                  />
                )}
              </Stack>
            )}
            {/* The message text (typically the server's "✅ Fertig" line) stays
                visible alongside the extended card. */}
            {!isPodcastGenerating && !isPodcastStartedMessage && (
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeKatex]}
                components={markdownComponents}>
                {messageText}
              </ReactMarkdown>
            )}
          </Box>
          {withFooter && !isPodcastGenerating && !isPodcastStartedMessage && messageText.trim() !== '' && (
            <Stack sx={{ width: '100%' }} data-testid='ai-assistant-message-footer'>
              <Stack
                sx={{
                  paddingY: spaces.sm,
                  paddingX: isExtended && !isTextEditorOpen ? spaces.sm : 0,
                  width: '100%',
                }}
                direction='row'
                justifyContent='space-between'>
                
                <Stack
                  direction='row'
                  gap={spaces.xs}
                  ref={messageActionsRef}
                  data-testid='ai-assistant-message-actions'
                >
                  {!hasArtifactCard &&
                  <AIAssistantIconButton
                    data-testid='ai-assistant-message-copy-button'
                    icon={isCopied ? 'check' : 'copy'}
                    tooltip={t('Copy')}
                    onClick={handleCopyButtonClick}
                    sx={isCopied ? { color: palette.success.main } : {}}
                  />}
                  {withRegenerateButton && !showPodcastCard && (
                    <AIAssistantIconButton
                      data-testid='ai-assistant-regenerate-button'
                      icon='refresh'
                      tooltip={t('Regenerate response')}
                      onClick={handleRegenerateButtonClick}
                    />
                  )}
                  {!hasArtifactCard &&
                  <OnboardingHint
                    onShow={handleTextEditorHintDisplayed }
                    id={onboardingHints.aiAssistant.textEditor}
                    description={t('Click here to edit the text directly. Then save as PDF or print.')}
                    placement='left-start' 
                    disablePortal={false}
                    maxWidth={350}
                    mobilePlacement='bottom-start'
                    tabletPlacement='bottom-start'
                    mobileMaxWidth={330}
                    offset={isXsScreen ? { x: 0, y: -50 } : { x: 0, y: 0 }}
                    preventOverflow
                    prerequisites={[onboardingHints.aiAssistant.chatHistoryButton]}
                    dontShow={
                      !isEditButtonVisible 
                        || currentSessionHints.has(onboardingHints.aiAssistant.chatHistoryButton)
                    }
                    onClose={() => addCurrentSessionHint(onboardingHints.aiAssistant.textEditor)}
                  >
                    <EditButtonWithHint
                      icon='aiPen'
                      tooltip={t('Edit')}
                      onClick={handleEditButtonClick}
                      data-testid='ai-assistant-edit-inline-message-button'
                    />
                  </OnboardingHint>
                  }
                </Stack>
                <Stack direction='row' gap={spaces.xs} sx={{ marginLeft: 'auto' }}>
                  <AIAssistantIconButton
                    icon={sentiment === 'positive' ? 'likeBold' : 'like'}
                    tooltip={t('Good answer')}
                    onClick={() => handleSentimentClick('positive')}
                    data-testid='ai-assistant-message-thumbs-up'
                  />
                  <AIAssistantIconButton
                    icon={sentiment === 'negative' ? 'dislikeBold' : 'dislike'}
                    tooltip={t('Bad answer')}
                    onClick={() => handleSentimentClick('negative')}
                    data-testid='ai-assistant-message-thumbs-down'
                  />
                </Stack>
              </Stack>
            </Stack>
          )}
        </>
      )}
      <ImagePreviewOverlay
        open={!!previewImage}
        src={previewImage?.src}
        alt={previewImage?.alt}
        onClose={() => setPreviewImage(null)}
        onDownload={handleImageDownloadTrack}
      />
    </Stack>
  );
};

export default AIAssistantMessage;
